/**
 * OffboardingListPage — Issue #363.
 *
 * Lists HR offboarding cases with mode / status / payroll-hold filters and a
 * shortcut to the executeDuePlannedCases sweep. Detail / create live behind
 * dedicated routes because each touches a sensitive lifecycle action.
 *
 * 红线 (AI GUIDE §6):
 *   - 列表只展示 employeeId / userId / mode / status / payrollHold / 时间；
 *     不展示员工姓名、邮箱、薪资明细。
 *   - 数据来自 generated api-client；没有前端假状态。
 *   - 403 / 409 / 404 / 400 走统一文案。
 */
import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { hasPermission, HR_PERMISSIONS } from '@/lib/hrPermissions';
import {
  HR_OFFBOARDING_CASE_STATUS_LABELS,
  HR_OFFBOARDING_MODE_LABELS,
  HR_OFFBOARDING_PAYROLL_HOLD_LABELS,
  useHrOffboardingCases,
  useHrOffboardingMutations,
  type HrOffboardingCaseDto,
} from '@/hooks/useHrOffboarding';

function fmtDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('zh-CN');
}

export default function OffboardingListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, HR_PERMISSIONS.OFFBOARDING_READ);
  const canWrite = hasPermission(permissions, HR_PERMISSIONS.OFFBOARDING_WRITE);

  const { items, total, loading, error, fetchCases } = useHrOffboardingCases();
  const { processDuePlanned, loading: mutating, error: mutationError } =
    useHrOffboardingMutations();

  const [modeFilter, setModeFilter] = useState<
    HrOffboardingCaseDto['mode'] | ''
  >('');
  const [statusFilter, setStatusFilter] = useState<
    HrOffboardingCaseDto['status'] | ''
  >('');
  const [payrollFilter, setPayrollFilter] = useState<
    HrOffboardingCaseDto['payrollHoldStatus'] | ''
  >('');
  const [page, setPage] = useState(1);
  const limit = 20;
  const [sweepMessage, setSweepMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!canRead) return;
    void fetchCases({
      mode: modeFilter || undefined,
      status: statusFilter || undefined,
      payrollHoldStatus: payrollFilter || undefined,
      page,
      limit,
    });
  }, [canRead, modeFilter, statusFilter, payrollFilter, page, fetchCases]);

  if (!canRead) return <AccessDenied resource="员工离职管理" />;

  const totalPages = Math.max(1, Math.ceil(total / limit));

  async function onRunSweep() {
    setSweepMessage(null);
    const result = await processDuePlanned();
    if (result) {
      setSweepMessage(
        `已处理 ${result.items.length} 条到期计划离职案件 (${fmtDateTime(result.processedAt)})`,
      );
      void fetchCases({
        mode: modeFilter || undefined,
        status: statusFilter || undefined,
        payrollHoldStatus: payrollFilter || undefined,
        page,
        limit,
      });
    }
  }

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">员工离职管理</h1>
          <p className="text-sm text-slate-500">
            支持计划离职与立即离职；交接清单未完成前薪资仅产生
            "暂缓发放 / 待复核" 状态，不写真实付款。
          </p>
        </div>
        <div className="flex gap-2">
          {canWrite ? (
            <button
              type="button"
              className="rounded-md border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
              disabled={mutating}
              onClick={onRunSweep}
            >
              执行到期计划离职 ↻
            </button>
          ) : null}
          {canWrite ? (
            <Link
              href="/hr/offboarding/create"
              className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
            >
              新建离职案件 →
            </Link>
          ) : null}
        </div>
      </header>

      {sweepMessage ? (
        <Card className="border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
          {sweepMessage}
        </Card>
      ) : null}

      {mutationError ? (
        <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {mutationError.message}
        </Card>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          模式
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={modeFilter}
            onChange={(e) => {
              setModeFilter(
                (e.target.value as HrOffboardingCaseDto['mode']) || '',
              );
              setPage(1);
            }}
          >
            <option value="">全部</option>
            {(
              Object.entries(HR_OFFBOARDING_MODE_LABELS) as [
                HrOffboardingCaseDto['mode'],
                string,
              ][]
            ).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          案件状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(
                (e.target.value as HrOffboardingCaseDto['status']) || '',
              );
              setPage(1);
            }}
          >
            <option value="">全部</option>
            {(
              Object.entries(HR_OFFBOARDING_CASE_STATUS_LABELS) as [
                HrOffboardingCaseDto['status'],
                string,
              ][]
            ).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          薪资状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={payrollFilter}
            onChange={(e) => {
              setPayrollFilter(
                (e.target.value as HrOffboardingCaseDto['payrollHoldStatus']) ||
                  '',
              );
              setPage(1);
            }}
          >
            <option value="">全部</option>
            {(
              Object.entries(HR_OFFBOARDING_PAYROLL_HOLD_LABELS) as [
                HrOffboardingCaseDto['payrollHoldStatus'],
                string,
              ][]
            ).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error.message}
        </Card>
      ) : null}

      <Card className="overflow-hidden">
        <table className="w-full table-fixed text-sm">
          <thead className="bg-slate-50 text-left text-slate-600">
            <tr>
              <th className="w-28 px-3 py-2">员工 ID</th>
              <th className="w-28 px-3 py-2">登录账号</th>
              <th className="w-24 px-3 py-2">模式</th>
              <th className="w-32 px-3 py-2">生效时间</th>
              <th className="w-28 px-3 py-2">案件状态</th>
              <th className="w-28 px-3 py-2">薪资状态</th>
              <th className="w-32 px-3 py-2">权限已回收</th>
              <th className="px-3 py-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{row.employeeId}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  {row.userId ?? '—'}
                </td>
                <td className="px-3 py-2">
                  {HR_OFFBOARDING_MODE_LABELS[row.mode]}
                </td>
                <td className="px-3 py-2">{fmtDateTime(row.effectiveAt)}</td>
                <td className="px-3 py-2">
                  {HR_OFFBOARDING_CASE_STATUS_LABELS[row.status]}
                </td>
                <td className="px-3 py-2">
                  {HR_OFFBOARDING_PAYROLL_HOLD_LABELS[row.payrollHoldStatus]}
                </td>
                <td className="px-3 py-2 text-xs">
                  {row.permissionRevokedAt
                    ? fmtDateTime(row.permissionRevokedAt)
                    : '尚未执行'}
                </td>
                <td className="px-3 py-2">
                  <Link
                    href={`/hr/offboarding/${row.id}`}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    查看详情
                  </Link>
                </td>
              </tr>
            ))}
            {items.length === 0 && !loading ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-3 py-6 text-center text-slate-400"
                >
                  暂无符合条件的离职案件
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </Card>

      <footer className="flex items-center justify-between text-sm text-slate-600">
        <span>
          共 {total} 条 / 第 {page} / {totalPages} 页
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            上一页
          </button>
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            下一页
          </button>
        </div>
      </footer>
    </div>
  );
}
